import React, {useContext} from 'react';
import {Redirect, Route, Switch, useRouteMatch} from 'react-router-dom';
import ChangePassword from '../../components/auth/ChangePassword';
import NavBar from '../../components/navbar/NavBar';
import AdminStart from '../../pages/admin/admin-start/AdminStart';
import Dashboard from '../../pages/dashboard/Dashboard';
import {useRoutingUtils} from '../RoutingUtils';
import {AdminRoute} from "../AdminRoute";
import AnalystDashboard from "../../pages/analyst/AnalystDashboard";
import {AccountRoute} from "./account/AccountRoute";
import UnitLevelModeling from "../../pages/unit-level-modeling/UnitLevelModeling";
import {FileUploadCreate} from "../../pages/admin/file-upload/FileUploadCreate";
import ReportsRoute from "../../pages/reports/ReportsRoute";
import {LineItemsReport} from '../../pages/reports/line-items-report/LineItemsReport';
import {DataHealthReport} from '../../pages/reports/data-health-report/DataHealthReport';
import PlanningDashboard from "../../pages/planning-dashboard/PlanningDashboard";
import PropertyDrivers from "../../pages/property-drivers/PropertyDrivers";
import TopCardTagSettings from '../../pages/admin/top-card-tag-settings/TopCardTagSettings';
import {AuthContext} from '../../contexts/AuthContext';
import FinancialReportsBulkExport from '../../pages/bulk-export/FinancialReportsBulkExport';
import OperationalReportsBulkExport from '../../pages/bulk-export/OperationalReportsBulkExport';
import BulkUpdate from '../../pages/bulk-update/BulkUpdate';
import BudgetSeasonManagement from "../../pages/budget-season-management/BudgetSeasonManagement";
import COAManagement from "../../pages/coa-management/COAManagement";
import UnitTypeManagement from "../../pages/unit-type-management/UnitTypeManagement";
import {COATags} from "../../pages/coa-tags/COATags";
import {CircularReferences} from "../../pages/circular-references/CircularReferences";
import {CustomItems} from "../../pages/custom-items/CustomItems";
import {MixPanelRouteWatcher} from "../../mixpanel/MixPanelRouteWatcher";
import {PropertyBudgetCompletion} from "../../pages/budget-completion/PropertyBudgetCompletion";
import {Users} from "../../pages/admin/users/Users";
import {SendBudget} from "../../pages/admin/jobs/SendBudget";
import ExecutiveSummaryReportManagement from "../../pages/executive-summary-report-management/ExecutiveSummaryReportManagement";
import {
    PropertyExecutiveSummaryRegularWrapper
} from "../../pages/property-executive-summary/PropertyExecutiveSummaryRegularWrapper";
import {FunPlayground} from '../../fun/FunPlayground';
import {PropertyManagement} from "../../pages/property-management/PropertyManagement";
import {PortfolioFinancialPage} from "../../pages/portfolio-financial/PortfolioFinancialPage";
import {
    checkAuthorization,
    hasAcquisitionPropertyAccess,
    userIsAdmin,
    userIsInternal
} from "../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../authorization/Authorizations";
import {ModelingAnalysis} from "../../pages/modeling-analysis/ModelingAnalysis";
import {BulkAssumptions} from '../../pages/bulk-assumptions/BulkAssumptions';
import {TemplatePropertyList} from "../../pages/property/template-property/TemplatePropertyList";
import AcquisitionProperties from "../../pages/acquisition-property/AcquisitionProperties";
import {ExternalChartOfAccountsMappingPage} from "../../pages/external-coa/mapping/ExternalChartOfAccountsMapping";
import css from "./mainRoute.module.scss";
import cn from "classnames";
import {UserWorkflow} from '../../components/user-workflows/UserWorkflow';
import {AuditLog} from "../../pages/admin/audit-log/AuditLog";
import {LineItemManagement} from "../../pages/admin/line-item-management/LineItemManagement";
import {getBudgetYearForProperty, useProperties} from "../../contexts/properties/PropertiesContext";
import {JurisdictionList} from "../../pages/admin/payroll/jurisdiction/JurisdictionList";
import {GPREGroupList} from "../../pages/admin/gpr-entities/GPREGroupList";
import {PayrollDefaults} from "../../pages/admin/payroll/defaults/PayrollDefaults";
import {PositionsList} from "../../pages/admin/payroll/positions/PositionsList";
import {ClientFeatures} from "../../pages/admin/internal/ClientFeatures";
import {ClientUnitCount} from "../../pages/admin/internal/ClientUnitCount";
import {isAcquisitionPropertiesEnabled, isInitialGprSetupEnabled, showExecutiveSummaryCustomNarrative} from "../../global/feature_checkers";
import {PayrollSettings} from "../../pages/payroll-settings/PayrollSettings";
import {GlobalOverrides} from "../../pages/admin/overrides/GlobalOverrides";

{/*
    TODO: Normalize to a correct top-level ThemeProvider (https://vizibly.atlassian.net/browse/APP-922), then wrap
    main route and remove ThemeProviders down in the code (unless rare cases when they're necessary)
*/}

export interface MainRouteProps {
    signedIn?: () => void
}
export const MainRoute: React.FC<MainRouteProps> = (params: MainRouteProps) =>
    <>
        <MixPanelRouteWatcher />
        <Contents {...params}></Contents>
    </>;

const Contents: React.FC<MainRouteProps> = () => {
    const {goto} = useRoutingUtils();
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const inWorkflow = useRouteMatch("/workflow/:userWorkflowTaskId/:type");

    return (
        <div className={css.appWrapper}>
            <div className={css.navWrapper}>
                { !inWorkflow &&
                    <NavBar
                        needSettings={goto('/admin/start')}
                        needPlan={goto('/setup/start')}
                    />
                }
            </div>

            <div id="viz-app-content-wrapper" className={cn(css.contentWrapper)}>
                <Switch>
                    <Route exact path="/auth/changepassword" component={ChangePassword} />
                    <AdminRoute exact path="/admin/start" component={() =>
                        <AdminStart
                            loggedOut={() => goto('/')}
                        />
                    } />
                    <Route exact path="/dashboard" component={() => <Dashboard />} />
                    <Route exact path="/line_items" component={() => <LineItemsReport />} />
                    <Route exact path="/data-health" component={() => <DataHealthReport />} />
                    <Route exact path="/hub/:tab?" component={AnalystDashboard} />
                    <Route exact path="/property_executive_summary" render={() => <PropertyExecutiveSummaryRegularWrapper/>}/>

                    {userIsAdmin(auth.user) && showExecutiveSummaryCustomNarrative(auth.user) &&
                        <Route exact path="/property_executive_summary/edit-summary" render={() => <PropertyExecutiveSummaryRegularWrapper/>}/>
                    }

                    <Route exact path="/portfolio_analysis" component={() => <PortfolioFinancialPage/>}/>

                    {/* Admin Payroll Routes */}
                    {userIsAdmin(auth.user) &&
                        <Route exact path="/settings/payroll-settings" component={() => <PayrollSettings />} />
                    }
                    {userIsAdmin(auth.user) &&
                            <Route exact path="/settings/payroll-jurisdictions" component={() => <JurisdictionList />} />
                    }
                    {userIsAdmin(auth.user) &&
                            <Route exact path="/settings/payroll-positions" component={() => <PositionsList />} />
                    }
                    {userIsAdmin(auth.user) &&
                            <Route exact path="/settings/payroll-defaults" component={() => <PayrollDefaults />} />
                    }
                    {(userIsInternal(auth.user) || (userIsAdmin(auth.user) && isInitialGprSetupEnabled(auth.user))) &&
                            <Route exact path="/settings/gpr-entity-groups" component={() => <GPREGroupList />} />
                    }

                    {userIsInternal(auth.user) &&
                        <Route exact path="/fun-playground" component={() => <FunPlayground />} />
                    }
                    <Route exact path="/revenue/:tab?/:type?" component={UnitLevelModeling} />
                    <AdminRoute exact path="/admin/users" component={Users} />
                    <AdminRoute exact path="/admin/send_budget" component={SendBudget} />
                    <AdminRoute exact path="/admin/file-upload/create" component={FileUploadCreate} />
                    <Route path="/account/:id/:type">
                        <AccountRoute />
                    </Route>
                    <Route path="/workflow/:userWorkflowTaskId/:type">
                        <UserWorkflow />
                    </Route>
                    {
                        /**
                         * 1. The original and unit level models use the normal operational routes.
                         * 2. The simplified model uses the new simplified-revenue routes.
                         */
                    }

                    <Route path="/operational/:page/:type" component={() =>
                        <AccountRoute />
                    } />

                    <Route path="/renovations/:page/:type" component={() =>
                        <AccountRoute />
                    } />

                    <Route path="/simplified-revenue/:page" component={() =>
                        <AccountRoute />
                    } />

                    <Route path="/payroll" component={() =>
                        <AccountRoute />
                    } />

                    <Route path="/affordable" component={() =>
                        <AccountRoute />
                    } />

                    <Route path="/circular-references">
                        <CircularReferences/>
                    </Route>

                    <Route path="/budget-completion">
                        <PropertyBudgetCompletion/>
                    </Route>

                    {userIsInternal(auth.user) &&
                        <Route path="/reports/mvr">
                            <ReportsRoute />
                        </Route>
                    }

                    <Route path="/modeling_analysis">
                        <ModelingAnalysis/>
                    </Route>


                    {checkAuthorization(auth.user, Authorizations.AccessAllProperties) &&
                        <Route path="/bulk-export/financial-reports" component={() =>
                            <FinancialReportsBulkExport />
                        } />
                    }

                    {checkAuthorization(auth.user, Authorizations.AccessAllProperties) &&
                        <Route path="/bulk-export/operational-reports" component={() =>
                            <OperationalReportsBulkExport />
                        } />
                    }

                    <Route
                        exact
                        path="/setup/start"
                        component={PlanningDashboard}
                    />

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/financial-metrics-management">
                            <TopCardTagSettings />
                        </Route>
                    }

                    {checkAuthorization(auth.user, Authorizations.AccessPropertyPlanManagement) &&
                        <Route path="/settings/property-plan-management">
                            <PropertyDrivers />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/bulk-assumptions">
                            <BulkAssumptions />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/lock-management">
                            <BulkUpdate />
                        </Route>
                    }

                    {checkAuthorization(auth.user, Authorizations.AccessBudgetSeasonManagement) &&
                        <Route path="/settings/budget-season-management">
                            <BudgetSeasonManagement />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/coa-management">
                            <COAManagement />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/account-tags-management">
                            <COATags />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/custom-item-management">
                            <CustomItems />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/admin/line-items-management">
                            <LineItemManagement budgetYear={auth.budgetYear} />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/admin/global-overrides">
                            <GlobalOverrides budgetYear={auth.budgetYear} />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/unit-type-management">
                            <UnitTypeManagement />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/property-management">
                            <PropertyManagement />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/template-properties">
                            <TemplatePropertyList budgetYear={budgetYear}/>
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/settings/executive-summary-report-management">
                            <ExecutiveSummaryReportManagement />
                        </Route>
                    }

                    {userIsAdmin(auth.user) &&
                        <Route path="/admin/audit-log">
                            <AuditLog />
                        </Route>
                    }

                    {hasAcquisitionPropertyAccess(auth.user) && isAcquisitionPropertiesEnabled(auth.user) &&
                        <Route path="/settings/acquisition-properties">
                            <AcquisitionProperties />
                        </Route>
                    }

                    {userIsInternal(auth.user) &&
                        <Route path="/settings/internal/client-features">
                            <ClientFeatures />
                        </Route>
                    }

                    {userIsInternal(auth.user) &&
                        <Route path="/settings/internal/client-unit-count">
                            <ClientUnitCount />
                        </Route>
                    }

                {/* TODO: Who has access to the mapping */}
                <Route path="/external-coa/:externalCoaId/mapping/:coaId">
                    <ExternalChartOfAccountsMappingPage />
                </Route>

                    <Route exact path="/">
                        <Redirect to="/setup/start" />
                    </Route>

                    <Route path="*">
                        <Redirect to="/setup/start" />
                    </Route>
                </Switch>
            </div>
        </div>
    );
};
